import type {
  BatchUpstreamCapabilitiesResponse,
  CreateUpstreamRequest,
  CreateUpstreamChannelRequest,
  CreateUpstreamChannelResponse,
  CreateUpstreamResponse,
  DeleteUpstreamChannelResponse,
  DeleteUpstreamResponse,
  DiscoverUpstreamCapabilitiesRequest,
  DiscoverUpstreamCapabilitiesResponse,
  GetUpstreamSkuRoutingSummaryResponse,
  GetUpstreamResponse,
  ListUpstreamChannelsResponse,
  ListProbeResultsResponse,
  ListUpstreamSkuCostsResponse,
  ListUpstreamCapabilitiesResponse,
  ProbeUpstreamCapabilityResponse,
  PreviewUpstreamCapabilityMutationRequest,
  PreviewUpstreamCapabilityMutationResponse,
  ListUpstreamsResponse,
  ProbeResultRecord,
  UpdateUpstreamRequest,
  UpdateUpstreamResponse,
  UpdateUpstreamChannelRequest,
  UpdateUpstreamChannelResponse,
  UpdateUpstreamCapabilityResponse,
  UpsertUpstreamSkuCostEntry,
  UpsertUpstreamSkuCostsResponse,
  SetUpstreamCapabilityProbeProtocolRequest,
  UpstreamModelCapabilityRecord,
  UpstreamCatalogResponse,
  UpstreamConfig,
  UpstreamRetirementImpactResponse,
  UpstreamChannelRecord,
  UpstreamSkuRoutingIdentityFilter,
  UpstreamSkuCostRecord,
} from '@nadoutong/contracts';
import { OpsApiError, opsApiRequest } from './client.ts';

/**
 * Typed wrappers around `ops-api /v1/ops/upstreams/*`.
 *
 * Every call reuses `opsApiRequest`, so session cookie handling, the
 * ADR-004 error envelope, and network-error normalization land in one
 * place. React Query hooks (see `useUpstreams.ts`) layer caching and
 * invalidation on top of these primitives.
 *
 * All wire types come from `@nadoutong/contracts` — ops-api proxies gateway
 * and platform responses, so the shapes are identical across layers.
 */

const BASE_PATH = '/v1/ops/upstreams';

export async function listUpstreams(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<UpstreamConfig[]> {
  const response = await opsApiRequest<ListUpstreamsResponse>(
    `${BASE_PATH}`,
    { signal },
    fetchImpl,
  );
  return response.upstreams;
}

/**
 * Fetch the per-upstream active route-binding catalog. Gateway filters out
 * soft-deleted upstreams from this query, so a just-deleted upstream briefly
 * disappears while its detail page still renders from the direct GET.
 */
export async function listCatalog(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<UpstreamCatalogResponse> {
  return opsApiRequest<UpstreamCatalogResponse>(
    `${BASE_PATH}/catalog`,
    { signal },
    fetchImpl,
  );
}

export async function getUpstream(
  id: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<UpstreamConfig> {
  const response = await opsApiRequest<GetUpstreamResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}`,
    { signal },
    fetchImpl,
  );
  return response.upstream;
}

export async function listUpstreamSkuCosts(
  id: string,
  upstreamChannelId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<UpstreamSkuCostRecord[]> {
  const params = new URLSearchParams({ upstream_channel_id: upstreamChannelId });
  const response = await opsApiRequest<ListUpstreamSkuCostsResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/sku-costs?${params.toString()}`,
    { signal },
    fetchImpl,
  );
  return [...response.costs];
}

export async function setUpstreamSkuCosts(
  id: string,
  costs: readonly UpsertUpstreamSkuCostEntry[],
  fetchImpl: typeof fetch,
): Promise<UpsertUpstreamSkuCostsResponse> {
  return opsApiRequest<UpsertUpstreamSkuCostsResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/sku-costs`,
    { method: 'PUT', body: { costs } },
    fetchImpl,
  );
}

export async function listUpstreamChannels(
  id: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<UpstreamChannelRecord[]> {
  const response = await opsApiRequest<ListUpstreamChannelsResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/channels`,
    { signal },
    fetchImpl,
  );
  return [...response.channels];
}

export async function createUpstreamChannel(
  id: string,
  body: CreateUpstreamChannelRequest,
  fetchImpl: typeof fetch,
): Promise<UpstreamChannelRecord> {
  const response = await opsApiRequest<CreateUpstreamChannelResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/channels`,
    { method: 'POST', body },
    fetchImpl,
  );
  return response.channel;
}

export async function updateUpstreamChannel(
  id: string,
  channelId: string,
  body: UpdateUpstreamChannelRequest,
  fetchImpl: typeof fetch,
): Promise<UpstreamChannelRecord> {
  const response = await opsApiRequest<UpdateUpstreamChannelResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/channels/${encodeURIComponent(channelId)}`,
    { method: 'PATCH', body },
    fetchImpl,
  );
  return response.channel;
}

export async function deleteUpstreamChannel(
  id: string,
  channelId: string,
  fetchImpl: typeof fetch,
): Promise<DeleteUpstreamChannelResponse> {
  return opsApiRequest<DeleteUpstreamChannelResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/channels/${encodeURIComponent(channelId)}`,
    { method: 'DELETE' },
    fetchImpl,
  );
}

/**
 * Retirement impact summary for the DangerZone confirmation dialog
 * (Epic #109 / P0.1). ops-api merges the gateway-owned slice (models
 * + traffic) with the platform-owned slice (funding batches) before
 * returning; we treat the merged payload as the single contract.
 */
export async function getUpstreamRetirementImpact(
  id: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<UpstreamRetirementImpactResponse> {
  return opsApiRequest<UpstreamRetirementImpactResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/retirement-impact`,
    { signal },
    fetchImpl,
  );
}

export async function createUpstream(
  body: CreateUpstreamRequest,
  fetchImpl: typeof fetch,
): Promise<UpstreamConfig> {
  const response = await opsApiRequest<CreateUpstreamResponse>(
    `${BASE_PATH}`,
    { method: 'POST', body },
    fetchImpl,
  );
  return response.upstream;
}

export async function updateUpstream(
  id: string,
  body: UpdateUpstreamRequest,
  fetchImpl: typeof fetch,
): Promise<UpstreamConfig> {
  const response = await opsApiRequest<UpdateUpstreamResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}`,
    { method: 'PATCH', body },
    fetchImpl,
  );
  return response.upstream;
}

export async function deleteUpstream(
  id: string,
  fetchImpl: typeof fetch,
): Promise<DeleteUpstreamResponse> {
  return opsApiRequest<DeleteUpstreamResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    fetchImpl,
  );
}

export async function listProbeResults(
  id: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
  limit?: number,
): Promise<ProbeResultRecord[]> {
  const query = limit !== undefined ? `?limit=${encodeURIComponent(limit)}` : '';
  const response = await opsApiRequest<ListProbeResultsResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/probes${query}`,
    { signal },
    fetchImpl,
  );
  return response.probes;
}

export async function listUpstreamCapabilities(
  id: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<UpstreamModelCapabilityRecord[]> {
  const response = await opsApiRequest<ListUpstreamCapabilitiesResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/capabilities`,
    { signal },
    fetchImpl,
  );
  return [...response.capabilities];
}

export async function discoverUpstreamCapabilities(
  id: string,
  body: DiscoverUpstreamCapabilitiesRequest,
  fetchImpl: typeof fetch,
): Promise<DiscoverUpstreamCapabilitiesResponse> {
  return opsApiRequest<DiscoverUpstreamCapabilitiesResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/capabilities/discover`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function probeUpstreamCapability(
  id: string,
  capabilityId: string,
  fetchImpl: typeof fetch,
): Promise<ProbeUpstreamCapabilityResponse> {
  return opsApiRequest<ProbeUpstreamCapabilityResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/capabilities/${encodeURIComponent(capabilityId)}/probe`,
    { method: 'POST', body: {} },
    fetchImpl,
  );
}

export async function batchProbeUpstreamCapabilities(
  id: string,
  capabilityIds: readonly string[],
  fetchImpl: typeof fetch,
): Promise<BatchUpstreamCapabilitiesResponse> {
  return opsApiRequest<BatchUpstreamCapabilitiesResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/capabilities/batch`,
    { method: 'POST', body: { action: 'probe', capability_ids: capabilityIds } },
    fetchImpl,
  );
}

export async function setUpstreamCapabilityProbeProtocol(
  id: string,
  capabilityId: string,
  body: SetUpstreamCapabilityProbeProtocolRequest,
  fetchImpl: typeof fetch,
): Promise<UpdateUpstreamCapabilityResponse> {
  return opsApiRequest<UpdateUpstreamCapabilityResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/capabilities/${encodeURIComponent(capabilityId)}/probe-protocol`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function batchSetUpstreamCapabilityProbeProtocol(
  id: string,
  capabilityIds: readonly string[],
  body: SetUpstreamCapabilityProbeProtocolRequest,
  fetchImpl: typeof fetch,
): Promise<BatchUpstreamCapabilitiesResponse> {
  return opsApiRequest<BatchUpstreamCapabilitiesResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/capabilities/batch`,
    {
      method: 'POST',
      body: {
        action: 'set_probe_protocol',
        capability_ids: capabilityIds,
        ...body,
      },
    },
    fetchImpl,
  );
}

export async function ignoreUpstreamCapability(
  id: string,
  capabilityId: string,
  fetchImpl: typeof fetch,
): Promise<UpdateUpstreamCapabilityResponse> {
  return opsApiRequest<UpdateUpstreamCapabilityResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/capabilities/${encodeURIComponent(capabilityId)}/ignore`,
    { method: 'POST', body: {} },
    fetchImpl,
  );
}

export async function batchIgnoreUpstreamCapabilities(
  id: string,
  capabilityIds: readonly string[],
  fetchImpl: typeof fetch,
): Promise<BatchUpstreamCapabilitiesResponse> {
  return opsApiRequest<BatchUpstreamCapabilitiesResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/capabilities/batch`,
    { method: 'POST', body: { action: 'ignore', capability_ids: capabilityIds } },
    fetchImpl,
  );
}

export async function unignoreUpstreamCapability(
  id: string,
  capabilityId: string,
  fetchImpl: typeof fetch,
): Promise<UpdateUpstreamCapabilityResponse> {
  return opsApiRequest<UpdateUpstreamCapabilityResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/capabilities/${encodeURIComponent(capabilityId)}/unignore`,
    { method: 'POST', body: {} },
    fetchImpl,
  );
}

export async function batchUnignoreUpstreamCapabilities(
  id: string,
  capabilityIds: readonly string[],
  fetchImpl: typeof fetch,
): Promise<BatchUpstreamCapabilitiesResponse> {
  return opsApiRequest<BatchUpstreamCapabilitiesResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/capabilities/batch`,
    { method: 'POST', body: { action: 'unignore', capability_ids: capabilityIds } },
    fetchImpl,
  );
}

export async function previewUpstreamCapabilityMutation(
  id: string,
  body: PreviewUpstreamCapabilityMutationRequest,
  fetchImpl: typeof fetch,
): Promise<PreviewUpstreamCapabilityMutationResponse> {
  return opsApiRequest<PreviewUpstreamCapabilityMutationResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/capabilities/impact-preview`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function getUpstreamSkuRoutingSummary(
  id: string,
  fetchImpl: typeof fetch,
  query: {
    readonly identityFilter?: UpstreamSkuRoutingIdentityFilter;
    readonly limit?: number;
    readonly offset?: number;
    readonly routingRevision?: string;
    readonly skuId?: string;
  } = {},
  signal?: AbortSignal,
): Promise<GetUpstreamSkuRoutingSummaryResponse> {
  const params = new URLSearchParams();
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.offset !== undefined) params.set('offset', String(query.offset));
  if (query.routingRevision !== undefined) params.set('routing_revision', query.routingRevision);
  if (query.skuId !== undefined) params.set('sku_id', query.skuId);
  if (query.identityFilter !== undefined) {
    params.set('capability_id', query.identityFilter.capability_id);
    params.set('vendor', query.identityFilter.vendor);
    params.set('model', query.identityFilter.model);
    params.set('account_id', query.identityFilter.account_id);
    params.set('key_bucket_id', query.identityFilter.key_bucket_id);
    params.set('upstream_channel_id', query.identityFilter.upstream_channel_id);
  }
  const search = params.size === 0 ? '' : `?${params.toString()}`;
  return opsApiRequest<GetUpstreamSkuRoutingSummaryResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/sku-routing-summary${search}`,
    { signal },
    fetchImpl,
  );
}

export const SKU_ROUTING_SUMMARY_CLIENT_PAGE_LIMIT = 100;
export const SKU_ROUTING_SUMMARY_CLIENT_MAX_IDENTITIES = 2_000;

export interface LoadedUpstreamSkuRoutingSummary extends GetUpstreamSkuRoutingSummaryResponse {
  /** True only when every page for the requested exact identity was loaded. */
  readonly complete: boolean;
  /** Client-side safety cap evidence; never silently treated as complete. */
  readonly client_truncated: boolean;
}

function sameIdentityFilter(
  left: UpstreamSkuRoutingIdentityFilter | null,
  right: UpstreamSkuRoutingIdentityFilter,
): boolean {
  return left !== null &&
    left.capability_id === right.capability_id &&
    left.vendor === right.vendor &&
    left.model === right.model &&
    left.account_id === right.account_id &&
    left.key_bucket_id === right.key_bucket_id &&
    left.upstream_channel_id === right.upstream_channel_id;
}

function assertSkuRoutingSummaryPage(
  page: GetUpstreamSkuRoutingSummaryResponse,
  upstreamId: string,
  identityFilter: UpstreamSkuRoutingIdentityFilter,
  expectedOffset: number,
  expectedRevision?: string,
  expectedSkuId: string | null = null,
): void {
  const { pagination } = page;
  if (
    page.upstream_id !== upstreamId ||
    !/^(0|[1-9]\d*)$/.test(page.routing_revision) ||
    (expectedRevision !== undefined && page.routing_revision !== expectedRevision) ||
    page.sku_id_filter !== expectedSkuId ||
    !sameIdentityFilter(page.identity_filter, identityFilter) ||
    pagination.limit !== SKU_ROUTING_SUMMARY_CLIENT_PAGE_LIMIT ||
    pagination.offset !== expectedOffset ||
    pagination.returned !== page.identities.length ||
    !Number.isSafeInteger(pagination.total) ||
    pagination.total < 0 ||
    pagination.offset + pagination.returned > pagination.total ||
    pagination.has_more !== (pagination.offset + pagination.returned < pagination.total) ||
    page.identities.some((row) =>
      row.upstream_id !== upstreamId ||
      ((row.capability_id != null && row.capability_id !== identityFilter.capability_id) ||
       (row.capability_id == null && (row.vendor !== identityFilter.vendor || row.model !== identityFilter.model))) ||
      row.account_id !== identityFilter.account_id ||
      row.key_bucket_id !== identityFilter.key_bucket_id ||
      row.upstream_channel_id !== identityFilter.upstream_channel_id
      || (expectedSkuId !== null && row.sku_id !== expectedSkuId)
    )
  ) {
    throw new Error('SKU routing summary page evidence mismatch');
  }
}

/**
 * Load a complete exact-identity summary through bounded pages. If the server
 * reports more than the client safety cap, return the first bounded page with
 * explicit truncation and let the drawer stay read-only.
 */
export async function loadUpstreamSkuRoutingIdentitySummary(
  id: string,
  identityFilter: UpstreamSkuRoutingIdentityFilter,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<LoadedUpstreamSkuRoutingSummary> {
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      return await loadUpstreamSkuRoutingIdentitySummarySnapshot(
        id,
        identityFilter,
        fetchImpl,
        signal,
      );
    } catch (error) {
      if (
        !(error instanceof OpsApiError) ||
        error.code !== 'ROUTING_SNAPSHOT_STALE' ||
        attempt === 2
      ) {
        throw error;
      }
    }
  }
  throw new Error('SKU routing summary snapshot retry budget exhausted');
}

/**
 * Recovery path for an identity whose all-SKU summary exceeds the client cap.
 * This is intentionally one request with no stale retry: the operator must see
 * and explicitly reload a changed revision before any write is re-enabled.
 */
export async function loadTargetedUpstreamSkuRoutingIdentitySummary(
  id: string,
  identityFilter: UpstreamSkuRoutingIdentityFilter,
  skuId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<LoadedUpstreamSkuRoutingSummary> {
  const normalizedSkuId = skuId.trim();
  if (normalizedSkuId.length === 0) throw new Error('targeted SKU id is required');
  const page = await getUpstreamSkuRoutingSummary(
    id,
    fetchImpl,
    {
      identityFilter,
      skuId: normalizedSkuId,
      limit: SKU_ROUTING_SUMMARY_CLIENT_PAGE_LIMIT,
      offset: 0,
    },
    signal,
  );
  assertSkuRoutingSummaryPage(page, id, identityFilter, 0, undefined, normalizedSkuId);
  if (
    page.pagination.total !== 1 ||
    page.pagination.returned !== 1 ||
    page.pagination.has_more ||
    page.identities.length !== 1 ||
    page.identities[0]?.sku_id !== normalizedSkuId
  ) {
    throw new Error('targeted SKU routing summary must echo one exact SKU identity');
  }
  return {
    ...page,
    complete: true,
    client_truncated: false,
  };
}

async function loadUpstreamSkuRoutingIdentitySummarySnapshot(
  id: string,
  identityFilter: UpstreamSkuRoutingIdentityFilter,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<LoadedUpstreamSkuRoutingSummary> {
  const first = await getUpstreamSkuRoutingSummary(
    id,
    fetchImpl,
    { identityFilter, limit: SKU_ROUTING_SUMMARY_CLIENT_PAGE_LIMIT, offset: 0 },
    signal,
  );
  assertSkuRoutingSummaryPage(first, id, identityFilter, 0);
  if (first.pagination.total > SKU_ROUTING_SUMMARY_CLIENT_MAX_IDENTITIES) {
    return { ...first, complete: false, client_truncated: true };
  }

  const identities = [...first.identities];
  let current = first;
  while (current.pagination.has_more) {
    const offset = identities.length;
    if (offset === current.pagination.offset || current.pagination.returned === 0) {
      throw new Error('SKU routing summary pagination did not advance');
    }
    const page = await getUpstreamSkuRoutingSummary(
      id,
      fetchImpl,
      {
        identityFilter,
        limit: SKU_ROUTING_SUMMARY_CLIENT_PAGE_LIMIT,
        offset,
        routingRevision: first.routing_revision,
      },
      signal,
    );
    assertSkuRoutingSummaryPage(page, id, identityFilter, offset, first.routing_revision);
    if (
      page.pagination.total !== first.pagination.total ||
      page.service_sku_count !== first.service_sku_count ||
      JSON.stringify(page.scope_counts) !== JSON.stringify(first.scope_counts)
    ) {
      throw new Error('SKU routing summary changed while loading pages');
    }
    identities.push(...page.identities);
    current = page;
  }

  const identityKeys = new Set(identities.map((row) => [
    row.sku_id,
    row.vendor,
    row.model,
    row.account_id,
    row.key_bucket_id,
    row.upstream_channel_id,
  ].join('\u0000')));
  if (identities.length !== first.pagination.total || identityKeys.size !== identities.length) {
    throw new Error('SKU routing summary pagination was incomplete or duplicated');
  }

  return {
    ...first,
    identities,
    pagination: {
      ...first.pagination,
      returned: identities.length,
      has_more: false,
    },
    complete: true,
    client_truncated: false,
  };
}
